import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  getNewFileFormGroup,
  getTitleNameFormDataAsPersonProject,
  patchTitleNameForm,
} from '@app/helpers/form.helper';
import { FileUploadControl } from '@app/models/form.models';
import { PersonSimpleWithRole } from '@app/models/project.models';
import {
  PersonProjectRequest,
  ProjectSetupARequest,
  ProjectSetupAResponse,
} from '@app/models/project-setup.models';
import { LocalizedValue } from '@app/models/shared.models';
import { FileType, PersonRoleType } from '@app/models/shared.enums';
import {
  TitleNameFormGroup,
  getTitleNameFormGroup,
} from '../../title-name-form/title-name-form.helper';

/** Section A person: title + name (from PUT scheme) plus position and email for display/edit. */
export interface SectionAPersonFormGroup extends TitleNameFormGroup {
  position: FormControl<string | null>;
  email: FormControl<string | null>;
}

export interface SectionAFormGroup {
  projectOwnerFullName: FormControl<string>;
  legalForm: FormControl<string>;
  ownershipStructure: FormControl<number | null>;
  projectOwner: FormGroup<SectionAPersonFormGroup>;
  contactEmail: FormControl<string>;
  validInformation: FormControl<boolean>;
  projectOwnerAdministrator: FormGroup<SectionAPersonFormGroup>;
  projectProfileOperatorFirst: FormGroup<SectionAPersonFormGroup>;
  projectProfileOperatorSecond: FormGroup<SectionAPersonFormGroup>;
  projectTEAORNumber: FormControl<string | null>;
  lastFinancialReports: FormArray<FormGroup<FileUploadControl>>;
  secondYearFinancialReports: FormArray<FormGroup<FileUploadControl>>;
  thirdYearFinancialReports: FormArray<FormGroup<FileUploadControl>>;
  projectDescription: FormControl<string>;
}

function sectionAPersonFormHasData(
  form: FormGroup<SectionAPersonFormGroup>,
): boolean {
  const firstName = form.controls.name.controls.firstName.value;
  return firstName != null && firstName !== '';
}

function getSectionAPersonFormGroup(
  fb: FormBuilder,
  required: boolean,
  roleCode: PersonRoleType,
): FormGroup<SectionAPersonFormGroup> {
  const base = getTitleNameFormGroup(fb, required, roleCode);
  return fb.group({
    title: base.controls.title,
    name: base.controls.name,
    position: fb.control<string | null>(null),
    email: fb.control<string | null>(null),
  }) as FormGroup<SectionAPersonFormGroup>;
}

function getSectionAPersonAsPersonProject(
  form: FormGroup<SectionAPersonFormGroup>,
): PersonProjectRequest {
  const base = getTitleNameFormDataAsPersonProject(
    form as unknown as FormGroup<TitleNameFormGroup>,
  );
  base.person.position = form.controls.position.value ?? null;
  base.person.email = form.controls.email.value ?? null;
  return base;
}

function getSectionAPersonsAsPersonProjectArray(
  forms: FormGroup<SectionAPersonFormGroup>[],
): PersonProjectRequest[] {
  const result: PersonProjectRequest[] = [];
  for (const form of forms) {
    if (sectionAPersonFormHasData(form)) {
      result.push(getSectionAPersonAsPersonProject(form));
    }
  }
  return result;
}

function patchSectionAPersonForm(
  form: FormGroup<SectionAPersonFormGroup>,
  person: PersonSimpleWithRole,
  nameTitles?: readonly LocalizedValue[],
): void {
  patchTitleNameForm(
    form as unknown as FormGroup<TitleNameFormGroup>,
    person,
    nameTitles,
  );
  form.controls.position.setValue(person.position ?? null);
  form.controls.email.setValue(person.email ?? null);
}

export function getSectionAFormGroup(
  fb: FormBuilder,
): FormGroup<SectionAFormGroup> {
  return fb.nonNullable.group({
    projectOwnerFullName: fb.nonNullable.control<string>('', [
      Validators.required,
    ]),
    legalForm: fb.nonNullable.control<string>('', [Validators.required]),
    ownershipStructure: fb.control<number | null>(null, [Validators.required]),
    projectOwner: getSectionAPersonFormGroup(fb, true, 'ProjVez'),
    contactEmail: fb.nonNullable.control<string>('', [Validators.required]),
    validInformation: fb.nonNullable.control<boolean>(false),
    projectOwnerAdministrator: getSectionAPersonFormGroup(
      fb,
      false,
      'ProjUgyvVez',
    ),
    projectProfileOperatorFirst: getSectionAPersonFormGroup(
      fb,
      false,
      'ProjProfilKez',
    ),
    projectProfileOperatorSecond: getSectionAPersonFormGroup(
      fb,
      false,
      'ProjProfilKez2',
    ),
    projectTEAORNumber: fb.control<string | null>(null, [Validators.required]),
    lastFinancialReports: fb.nonNullable.array<FormGroup<FileUploadControl>>([
      getNewFileFormGroup(fb, FileType.projectFinancialPlan1y, false, true),
    ]),
    secondYearFinancialReports: fb.nonNullable.array<
      FormGroup<FileUploadControl>
    >([getNewFileFormGroup(fb, FileType.projectFinancialPlan2y, false, true)]),
    thirdYearFinancialReports: fb.nonNullable.array<
      FormGroup<FileUploadControl>
    >([getNewFileFormGroup(fb, FileType.projectFinancialPlan3y, false, true)]),
    projectDescription: fb.nonNullable.control<string>('', [
      Validators.required,
    ]),
  });
}

export function getSectionAUpdateData(
  formGroup: FormGroup<SectionAFormGroup>,
): ProjectSetupARequest {
  return {
    companyName: formGroup.controls.projectOwnerFullName.value,
    companyTypeId: formGroup.controls.legalForm.value,
    email: formGroup.controls.contactEmail.value,
    ownerAcceptEveryInfoGiven: formGroup.controls.validInformation.value,
    ownershipStructure: formGroup.controls.ownershipStructure.value!,
    description: formGroup.controls.projectDescription.value,
    teaorNumbers: formGroup.controls.projectTEAORNumber.value!,
    persons: getSectionAPersonsAsPersonProjectArray([
      formGroup.controls.projectOwner,
      formGroup.controls.projectOwnerAdministrator,
      formGroup.controls.projectProfileOperatorFirst,
      formGroup.controls.projectProfileOperatorSecond,
    ]),
  };
}

export function patchSectionAForm(
  formGroup: FormGroup<SectionAFormGroup>,
  data: ProjectSetupAResponse,
  nameTitles?: readonly LocalizedValue[],
): void {
  formGroup.controls.projectOwnerFullName.setValue(data.companyName);
  formGroup.controls.legalForm.setValue(data.companyTypeId);
  formGroup.controls.ownershipStructure.setValue(data.ownershipStructure);
  formGroup.controls.contactEmail.setValue(data.email);
  formGroup.controls.validInformation.setValue(data.ownerAcceptEveryInfoGiven);
  formGroup.controls.projectTEAORNumber.setValue(data.teaorNumbers);
  formGroup.controls.projectDescription.setValue(data.description);
  data.persons.forEach((person) => {
    switch (person.role.code as PersonRoleType) {
      case 'ProjVez':
        patchSectionAPersonForm(
          formGroup.controls.projectOwner,
          person,
          nameTitles,
        );
        break;
      case 'ProjUgyvVez':
        patchSectionAPersonForm(
          formGroup.controls.projectOwnerAdministrator,
          person,
          nameTitles,
        );
        break;
      case 'ProjProfilKez':
        patchSectionAPersonForm(
          formGroup.controls.projectProfileOperatorFirst,
          person,
          nameTitles,
        );
        break;
      case 'ProjProfilKez2':
        patchSectionAPersonForm(
          formGroup.controls.projectProfileOperatorSecond,
          person,
          nameTitles,
        );
        break;
    }
  });
  data.files.forEach((file) => {
    switch (file.fileType) {
      case 2:
        formGroup.controls.lastFinancialReports.setValue([
          {
            fileType: file.fileType,
            id: file.id,
            isPublic: true,
            title: file.name,
            description: file.description,
            file: null,
          },
        ]);
        break;
      case 3:
        formGroup.controls.secondYearFinancialReports.setValue([
          {
            fileType: file.fileType,
            id: file.id,
            isPublic: true,
            title: file.name,
            description: file.description,
            file: null,
          },
        ]);
        break;
      case 4:
        formGroup.controls.thirdYearFinancialReports.setValue([
          {
            fileType: file.fileType,
            id: file.id,
            isPublic: true,
            title: file.name,
            description: file.description,
            file: null,
          },
        ]);
        break;
    }
  });
}
